<script lang="ts">
  import getCompletion from '../../utils/get-completion';
  import Button from '../button/Button.svelte';
  import LoadingSpinner from '../loader/LoadingSpinner.svelte';
  import RephrasingInfo from './RephrasingInfo.svelte';

  export let label = '';
  export let placeholder = '';
  export let value: string; // the actual content of the text-area
  export let id = '';
  export let required = false;

  export let language: 'de' | 'en' = 'de';
  export let onTranslate: (
    translation: string,
    language: 'de' | 'en'
  ) => void = () => {};

  let rephrasingReponse = ''; // for previewing the rephrased description text inside the Note component
  let rephrasingLoading = false;
  let translationLoading = false;

  const translateButtonText =
    language === 'de' ? 'Translate to EN' : 'Translate to DE';

  async function handleRephrase(event: Event, shouldTranslate: boolean) {
    event.preventDefault();

    if (rephrasingLoading && translationLoading) {
      return;
    }

    if (shouldTranslate) {
      translationLoading = true;
    } else {
      rephrasingLoading = true;
    }

    // The completion response - because the cloud function may experience an error
    // this response value may be undefined

    try {
      const response = await getCompletion({
        descriptionText: value,
        language,
        shouldTranslate
      });

      if (response && !shouldTranslate) {
        rephrasingReponse = response;
      } else if (response && shouldTranslate) {
        onTranslate(response, language);
      }
    } catch (error) {
      console.error(error);
    } finally {
      translationLoading = false;
      rephrasingLoading = false;
    }
  }

  /**
   * Reset the response
   */
  const resetResponse = () => (rephrasingReponse = '');

  /**
   * Assign the previously rephrased text to the textarea
   */
  const handleAccept = () => {
    value = rephrasingReponse;
    resetResponse();
  };
</script>

<style lang="postcss">
  .container {
    display: grid;
    gap: 0.25rem;
    margin-bottom: 1.5rem;
  }

  .button-container {
    margin-block-start: 1rem;

    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    gap: 1rem;
    width: 100%;
  }

  .textarea {
    width: 100%;

    &:focus,
    &:active {
      box-shadow: none;
      border: 1px solid var(--ubilabs-blue-light-3);
    }
  }

  @media (min-width: 768px) {
    .container {
      grid-template-columns: 1fr 2fr 1fr;
      align-items: start;
      gap: 1.5rem;
    }

    .container > div:first-child {
      display: flex;
      align-items: center;
      justify-content: end;
      min-height: var(--ubilabs-input-height);
    }
  }
</style>

<div class="container {$$props.class}">
  <div>
    {#if label}
      <label for={id}>{label}{required ? '*' : ''}</label>
    {/if}
  </div>
  <div>
    <textarea
      class="textarea"
      name={id}
      {id}
      {placeholder}
      {required}
      rows="6"
      bind:value />
    {#if rephrasingReponse}
      <RephrasingInfo
        onCancel={resetResponse}
        onRetry={handleRephrase}
        onAccept={handleAccept}
        loading={rephrasingLoading || translationLoading}>
        {rephrasingReponse}
      </RephrasingInfo>
    {/if}
    <div class="button-container">
      {#if !rephrasingReponse && value}
        <Button
          type="button"
          disabled={rephrasingLoading || translationLoading || !value}
          on:click={event => handleRephrase(event, false)}>
          {#if rephrasingLoading}
            <LoadingSpinner />
          {:else}
            Rephrase with AI
          {/if}
        </Button>
        <Button
          type="button"
          disabled={rephrasingLoading || translationLoading || !value}
          on:click={event => handleRephrase(event, true)}>
          {#if translationLoading}
            <LoadingSpinner />
          {:else}
            {translateButtonText}
          {/if}
        </Button>
      {/if}
    </div>
  </div>

  <div />
</div>
